export const maxShaderClipPointCount = 64;

/**
 * Resample a closed polygon to at most `maxPointCount` vertices, spaced evenly
 * along the perimeter. Used to fit clip polygons into fixed-size shader uniforms.
 */
export function fitClipPolygonToUniformLimit(
  polygon: Array<{ x: number; y: number }>,
  maxPointCount: number,
): Array<{ x: number; y: number }> {
  if (polygon.length <= maxPointCount) {
    return polygon;
  }

  const closed = [...polygon, polygon[0]!];
  const segmentLengths: number[] = [];
  let perimeter = 0;
  for (let index = 0; index < polygon.length; index += 1) {
    const from = closed[index]!;
    const to = closed[index + 1]!;
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    segmentLengths.push(length);
    perimeter += length;
  }

  if (perimeter <= 0) {
    return polygon.slice(0, maxPointCount);
  }

  const fitted: Array<{ x: number; y: number }> = [];
  let segmentIndex = 0;
  let segmentStartDistance = 0;
  for (let pointIndex = 0; pointIndex < maxPointCount; pointIndex += 1) {
    const targetDistance = (pointIndex / maxPointCount) * perimeter;
    while (
      segmentIndex < segmentLengths.length - 1 &&
      segmentStartDistance + (segmentLengths[segmentIndex] ?? 0) < targetDistance
    ) {
      segmentStartDistance += segmentLengths[segmentIndex] ?? 0;
      segmentIndex += 1;
    }

    const from = closed[segmentIndex]!;
    const to = closed[segmentIndex + 1]!;
    const segmentLength = segmentLengths[segmentIndex] ?? 0;
    const t = segmentLength > 0 ? (targetDistance - segmentStartDistance) / segmentLength : 0;
    fitted.push({
      x: from.x + (to.x - from.x) * t,
      y: from.y + (to.y - from.y) * t,
    });
  }

  return fitted;
}
